import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import AdminSectionTitle from '../../../components/admin/AdminSectionTitle';
import UnitType from '../../../constants/UnitType';
import UserRole from '../../../constants/UserRole';
import ApprovalStatus from '../../../constants/ApprovalStatus';
import RentalStatus from '../../../constants/RentalStatus';
import { UserContext } from '../../../contexts/UserContext';
import useAdminSizeDetector from '../../../hooks/useAdminSizeDetector';
import { usePrompt } from '../../../hooks/blocker';
import * as api from '../../../apis';

const KakaoNotificationSetting = () => {
	const { user } = useContext(UserContext);
	const { isDesktop, isTabletHorizontal, isTabletVertical, isMobile } = useAdminSizeDetector();
	const [unitType, setUnitType] = useState(null);
	const [kakaoNotificationSetting, setKakaoNotificationSetting] = useState(null);
	const [messageList, setMessageList] = useState([]);
	const [isModify, setIsModify] = useState(false);

	useEffect(() => {
		let defaultUnitType = UnitType.ROOM;
		if (UserRole.isSystemAdminRole(user?.user_role) || UserRole.isRoomAdminRole(user?.user_role)) {
			defaultUnitType = UnitType.ROOM;
		} else if (UserRole.isProductAdminRole(user?.user_role)) {
			defaultUnitType = UnitType.PRODUCT;
		} else if (UserRole.isConsultAdminRole(user?.user_role)) {
			defaultUnitType = UnitType.CONSULT;
		}
		setUnitType(defaultUnitType);
		getKakaoNotificationSetting(defaultUnitType);
		getKakaoNotificationMessageList(defaultUnitType);
	}, [user?.user_role]);

	usePrompt('알림 설정을 저장하지 않고\n페이지를 벗어나시겠습니까?', isModify);

	const getKakaoNotificationSetting = (targetUnitType) => {
		api.getKakaoNotificationSetting(targetUnitType)
			.then((res) => {
				if (res && res.data) {
					setKakaoNotificationSetting(res.data);
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const getKakaoNotificationMessageList = (targetUnitType) => {
		api.getKakaoNotificationMessageList(targetUnitType)
			.then((res) => {
				if (res && res.data) {
					setMessageList(res.data);
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const onUnitTypeChange = (changedUnitType) => {
		setUnitType(changedUnitType);
		getKakaoNotificationSetting(changedUnitType);
		getKakaoNotificationMessageList(changedUnitType);
	};

	const getMessageData = (status, target, key) => {
		const message = messageList?.find(
			(m) => m.status === status && m.send_target === target
		);
		return message?.[key] ?? '';
	};

	const changeMessageData = (status, target, key, value) => {
		setIsModify(true);
		setMessageList(
			messageList.map((message) => {
				if (message.status === status && message.send_target === target) {
					return { ...message, [key]: value };
				}
				return message;
			})
		);
	};

	const onChangeUseKakao = (value) => {
		setIsModify(true);
		setKakaoNotificationSetting({
			...kakaoNotificationSetting,
			use_notification: value,
		});
	};

	const onChangeSenderNumber = (value) => {
		setIsModify(true);
		setKakaoNotificationSetting({
			...kakaoNotificationSetting,
			sender_number: value,
		});
	};

	const onClickSave = () => {
		if (!unitType) return;
		const settingPayload = {
			sender_number: kakaoNotificationSetting?.sender_number ?? null,
			use_notification: kakaoNotificationSetting?.use_notification ?? false,
		};
		Promise.all([
			api.updateKakaoNotificationSetting(unitType, settingPayload),
			api.updateKakaoNotificationMessageList(unitType, messageList),
		])
			.then((res) => {
				setIsModify(false);
				alert('저장되었습니다.');
			})
			.catch((err) => {
				console.error(err);
				alert('저장에 실패하였습니다.');
			});
	};

	const renderTableHeader = () => {
		if (isDesktop || isTabletHorizontal) {
			return (
				<tr>
					<th style={{ width: '216px' }}>발송항목</th>
					<th style={{ width: '116px' }}>발송시점</th>
					<th style={{ width: '72px' }}>발송대상</th>
					<th>제목</th>
					<th>내용</th>
					<th style={{ width: '100px' }}>자동발송여부</th>
				</tr>
			);
		}
		if (isTabletVertical) {
			return (
				<tr>
					<th style={{ minWidth: '128px' }}>발송항목</th>
					<th style={{ minWidth: '88px' }}>발송시점</th>
					<th style={{ minWidth: '64px' }}>발송대상</th>
					<th style={{ minWidth: '180px' }}>제목</th>
					<th style={{ minWidth: '180px' }}>내용</th>
					<th style={{ minWidth: '88px' }}>자동발송여부</th>
				</tr>
			);
		}
		if (isMobile) {
			return (
				<tr>
					<th style={{ minWidth: '128px' }}>발송항목</th>
					<th style={{ minWidth: '88px' }}>발송시점</th>
					<th style={{ minWidth: '64px' }}>발송대상</th>
					<th style={{ minWidth: '200px' }}>제목</th>
					<th style={{ minWidth: '200px' }}>내용</th>
					<th style={{ minWidth: '88px' }}>자동발송여부</th>
				</tr>
			);
		}
	};

	const renderMessageData = (status, target = 'user') => (
		<>
			<MessageTableData>
				<MessageTextArea
					rows={4}
					value={getMessageData(status, target, 'title')}
					onChange={(e) => changeMessageData(status, target, 'title', e.currentTarget.value)}
				/>
			</MessageTableData>
			<MessageTableData>
				<MessageTextArea
					rows={isTabletVertical ? 5 : 4}
					value={getMessageData(status, target, 'content')}
					onChange={(e) => changeMessageData(status, target, 'content', e.currentTarget.value)}
				/>
			</MessageTableData>
			<td>
				<Checkbox
					type="checkbox"
					checked={getMessageData(status, target, 'use_message') ?? false}
					onChange={(e) => changeMessageData(status, target, 'use_message', e.target.checked)}
				/>
			</td>
		</>
	);

	const renderRoomKakaoNotificationMessage = () => {
		if (kakaoNotificationSetting?.use_notification !== true) return null;
		return (
			<MessageContainer>
				<AdminSectionTitle title="예약 및 승인 알림" />
				<SenderSection>
					<SenderRow>
						<TextInputLabel>발신 번호</TextInputLabel>
						<TextInput
							type="text"
							value={kakaoNotificationSetting?.sender_number ?? ''}
							placeholder="발신번호를 입력해주세요"
							onChange={(e) => onChangeSenderNumber(e.currentTarget.value)}
						/>
						<HelpText>*발신번호는 알림톡 내용에 포함되어 발송됩니다.</HelpText>
					</SenderRow>
				</SenderSection>
				<MessageTableContainer>
					<MessageTable>
						<thead>{renderTableHeader()}</thead>
						<tbody>
							<tr>
								<StatusTableData>
									<StatusTitle>승인대기</StatusTitle>
									<StatusListText>예약 신청 완료상태</StatusListText>
									<StatusListText>승인 대기 상태로 접수 시</StatusListText>
								</StatusTableData>
								<td>승인상태 변경</td>
								<td>신청자</td>
								{renderMessageData(ApprovalStatus.PENDING, 'user')}
							</tr>
							<tr>
								<StatusTableData>
									<StatusTitle>승인거부</StatusTitle>
									<StatusSubTitle>관리자가 신청 승인 거부</StatusSubTitle>
								</StatusTableData>
								<td>승인상태 변경</td>
								<td>신청자</td>
								{renderMessageData(ApprovalStatus.REJECTED, 'user')}
							</tr>
							<tr>
								<StatusTableData>
									<StatusTitle>예약취소</StatusTitle>
									<StatusSubTitle>본인/관리자가 예약 취소</StatusSubTitle>
								</StatusTableData>
								<td>승인상태 변경</td>
								<td>신청자</td>
								{renderMessageData(ApprovalStatus.CANCELED, 'user')}
							</tr>
							<tr>
								<StatusTableData>
									<StatusTitle>승인완료</StatusTitle>
									<StatusSubTitle>관리자/자동 신청 승인</StatusSubTitle>
								</StatusTableData>
								<td>승인상태 변경</td>
								<td>신청자</td>
								{renderMessageData(ApprovalStatus.APPROVED, 'user')}
							</tr>
							<tr>
								<StatusTableData>
									<StatusTitle>[운영자] 승인대기</StatusTitle>
									<StatusListText>예약 신청 완료상태</StatusListText>
									<StatusListText>승인 대기 상태로 접수 시</StatusListText>
								</StatusTableData>
								<td>승인상태 변경</td>
								<td>운영자</td>
								{renderMessageData(ApprovalStatus.PENDING, 'admin')}
							</tr>
						</tbody>
					</MessageTable>
				</MessageTableContainer>
			</MessageContainer>
		);
	};

	const renderProductKakaoNotificationMessage = () => {
		if (kakaoNotificationSetting?.use_notification !== true) return null;
		return (
			<MessageContainer>
				<AdminSectionTitle title="수령 및 반납 알림" />
				<MessageTableContainer>
					<MessageTable>
						<thead>{renderTableHeader()}</thead>
						<tbody>
							<tr>
								<StatusTableData>
									<StatusTitle>수령 지연</StatusTitle>
									<StatusSubTitle>수령일 1일 후 자동 발송</StatusSubTitle>
								</StatusTableData>
								<td>수령/반납 상태 변경</td>
								<td>신청자</td>
								{renderMessageData(RentalStatus.RENTAL_DELAY, 'user')}
							</tr>
							<tr>
								<StatusTableData>
									<StatusTitle>수령 완료/반납 안내</StatusTitle>
									<StatusSubTitle>수령 완료 상태로 변경시</StatusSubTitle>
								</StatusTableData>
								<td>수령/반납 상태 변경</td>
								<td>신청자</td>
								{renderMessageData(RentalStatus.RENTAL_COMPLETE, 'user')}
							</tr>
							<tr>
								<StatusTableData>
									<StatusTitle>반납 지연</StatusTitle>
									<StatusSubTitle>반납기일 후 자동 발송</StatusSubTitle>
								</StatusTableData>
								<td>수령/반납 상태 변경</td>
								<td>신청자</td>
								{renderMessageData(RentalStatus.RETURN_DELAY, 'user')}
							</tr>
							<tr>
								<StatusTableData>
									<StatusTitle>반납 완료</StatusTitle>
									<StatusSubTitle>관리자가 수령/반납 상태를</StatusSubTitle>
									<StatusSubTitle>반납완료로 변경함</StatusSubTitle>
								</StatusTableData>
								<td>수령/반납 상태 변경</td>
								<td>신청자</td>
								{renderMessageData(RentalStatus.RETURN_COMPLETE, 'user')}
							</tr>
							<tr>
								<StatusTableData>
									<StatusTitle>[운영자] 수령 지연</StatusTitle>
									<StatusSubTitle>수령일 1일 후 자동 발송</StatusSubTitle>
								</StatusTableData>
								<td>수령/반납 상태 변경</td>
								<td>운영자</td>
								{renderMessageData(RentalStatus.RENTAL_DELAY, 'admin')}
							</tr>
							<tr>
								<StatusTableData>
									<StatusTitle>[운영자] 반납 지연</StatusTitle>
									<StatusSubTitle>반납기일 후 자동 발송</StatusSubTitle>
								</StatusTableData>
								<td>수령/반납 상태 변경</td>
								<td>운영자</td>
								{renderMessageData(RentalStatus.RETURN_DELAY, 'admin')}
							</tr>
						</tbody>
					</MessageTable>
				</MessageTableContainer>
			</MessageContainer>
		);
	};

	return (
		<InnerContent>
			<UnitTypeSelectContainer>
				<SubTitle>예약 유형 선택</SubTitle>
				<UnitTypeSelect
					value={unitType ?? ''}
					onChange={(e) => onUnitTypeChange(e.currentTarget.value)}
				>
					{(UserRole.isSystemAdminRole(user?.user_role) || UserRole.isRoomAdminRole(user?.user_role)) && (
						<option value={UnitType.ROOM}>시설</option>
					)}
					{JSON.parse(process.env.REACT_APP_USE_TYPE_PRODUCT) &&
						(UserRole.isSystemAdminRole(user?.user_role) ||
							UserRole.isProductAdminRole(user?.user_role)) && (
							<option value={UnitType.PRODUCT}>장비</option>
						)}
					{JSON.parse(process.env.REACT_APP_USE_TYPE_CONSULT) &&
						(UserRole.isSystemAdminRole(user?.user_role) ||
							UserRole.isConsultAdminRole(user?.user_role)) && (
							<option value={UnitType.CONSULT}>상담</option>
						)}
				</UnitTypeSelect>
			</UnitTypeSelectContainer>
			<Separator />
			<KakaoSettingSection>
				<AdminSectionTitle title="카카오 알림톡 사용여부" />
				<RadioBoxGroupRow>
					<RadioBoxGroup>
						<input
							type="radio"
							id="kakao-use-yes"
							name="kakao-use"
							checked={kakaoNotificationSetting?.use_notification === true}
							onChange={() => onChangeUseKakao(true)}
						/>
						<label htmlFor="kakao-use-yes">사용함</label>
					</RadioBoxGroup>
					<RadioBoxGroup>
						<input
							type="radio"
							id="kakao-use-no"
							name="kakao-use"
							checked={kakaoNotificationSetting?.use_notification === false}
							onChange={() => onChangeUseKakao(false)}
						/>
						<label htmlFor="kakao-use-no">사용안함</label>
					</RadioBoxGroup>
					<SaveButton onClick={onClickSave}>저장</SaveButton>
				</RadioBoxGroupRow>
			</KakaoSettingSection>
			{renderRoomKakaoNotificationMessage()}
			{unitType === UnitType.PRODUCT && renderProductKakaoNotificationMessage()}
		</InnerContent>
	);
};

const InnerContent = styled.div`
	width: 100%;
	padding: 0;
	margin: 0;
`;

const UnitTypeSelectContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	height: 54px;
	padding: 8px 20px;
	margin-bottom: 20px;
	background-color: #f7f7f7;
	border: 1px solid #dddddd;
`;

const SubTitle = styled.div`
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	line-height: 17px;
	color: #000000;
`;

const UnitTypeSelect = styled.select`
	max-width: 210px;
	width: 100%;
	height: 35px;
	padding: 8px;
	border: 1px solid #dddddd;
	border-radius: 5px;
	appearance: none;
	background-image: linear-gradient(45deg, transparent 50%, #000000 50%),
		linear-gradient(135deg, #000000 50%, transparent 50%);
	background-position: calc(100% - 20px) calc(1em + 0px), calc(100% - 15px) calc(1em + 0px), calc(100% - 2.5em) 0.5em;
	background-size: 5px 5px, 5px 5px, 1px 1.5em;
	background-repeat: no-repeat;
	font-family: 'Pretendard';
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;
	color: #333333;
`;

const Separator = styled.hr`
	color: #6f6f6f;
	margin-block-start: 20px;
	margin-block-end: 20px;
`;

const KakaoSettingSection = styled.div`
	padding: 0;
	margin-bottom: 20px;
`;

const RadioBoxGroupRow = styled.div`
	height: 35px;
	display: flex;
	align-items: center;
	gap: 20px;
	margin-top: 8px;
`;

const RadioBoxGroup = styled.div`
	display: flex;
	align-items: center;

	input {
		cursor: pointer;
		width: 15px;
		height: 15px;
	}

	label {
		cursor: pointer;
		margin-left: 10px;
		color: #666666;
		font-family: 'Noto Sans';
		font-size: 15px;
		font-weight: 400;
	}
`;

const SenderSection = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 6px;
	width: 100%;
	margin-top: 20px;
`;

const SenderRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	flex-wrap: wrap;

	@media only screen and (max-width: 479.98px) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const TextInputLabel = styled.div`
	font-family: 'Pretendard';
	font-weight: 500;
	font-size: 14px;
	line-height: 17px;
	color: #333333;
	flex-shrink: 0;
`;

const TextInput = styled.input`
	width: 200px;
	height: 35px;
	padding: 8px;
	border: 1px solid #dddddd;
	background-color: #ffffff;
	font-family: 'Pretendard';
	font-size: 14px;
	line-height: 17px;
	color: #666666;
	flex-shrink: 0;
`;

const HelpText = styled.span`
	font-family: 'Pretendard';
	font-size: 12px;
	line-height: 14px;
	color: #666666;
	margin-left: 4px;
`;

const Button = styled.div`
	height: 36px;
	padding: 0 15px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #454655;
	border-radius: 5px;
	cursor: pointer;
	font-family: 'Pretendard';
	font-weight: 500;
	font-size: 16px;
	line-height: 19px;
	white-space: nowrap;
	color: #ffffff;

	&:hover {
		background-color: #1d1e31;
	}
`;

const SaveButton = styled(Button)`
	margin-left: auto;
`;

const MessageContainer = styled.div`
	margin-top: 20px;
	margin-bottom: 30px;
`;

const MessageTableContainer = styled.div`
	width: 100%;
	@media only screen and (max-width: 767.98px) {
		max-width: 728px;
		overflow-x: scroll;
	}
`;

const MessageTable = styled.table`
	width: 100%;
	margin-top: 20px;
	border-collapse: collapse;
	border-top: 2px solid #0e498f;
	text-align: center;

	thead th {
		height: 45px;
		background-color: #f6f8fb;
		border-right: 1px solid #dddddd;
		font-family: 'Pretendard';
		font-weight: 600;
		font-size: 14px;
		line-height: 17px;
		color: #0e498f;
		:first-child {
			border-left: 1px solid #dddddd;
		}
	}
	thead tr {
		border-bottom: 1px solid #dcdcdc;
	}
	tbody td {
		height: 45px;
		border-right: 1px solid #dddddd;
		padding: 6px;
		font-family: 'Pretendard';
		font-size: 14px;
		line-height: 17px;
		color: #333333;
		word-break: break-all;
		:first-child {
			padding: 0 12px;
			border-left: 1px solid #dddddd;
		}
		:nth-child(2),
		:nth-child(3) {
			padding: 0 2px;
		}
	}
	tbody tr {
		border-bottom: 1px solid #dddddd;
	}
`;

const StatusTableData = styled.td`
	text-align: left;
	padding-left: 12px !important;
`;

const StatusTitle = styled.div`
	font-family: 'Pretendard';
	font-weight: 600;
	font-size: 14px;
	line-height: 17px;
	color: #333333;
`;

const StatusSubTitle = styled.div`
	font-family: 'Pretendard';
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;
	color: #333333;
`;

const StatusListText = styled.li`
	font-family: 'Pretendard';
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;
	color: #333333;
	margin-left: 15px;
`;

const MessageTableData = styled.td`
	padding: 15px !important;
	@media only screen and (max-width: 479.98px) {
		padding: 5px !important;
	}
`;

const MessageTextArea = styled.textarea`
	width: 100%;
	padding: 8px;
	border: 1px solid #dddddd;
	border-radius: 5px;
	resize: none;
	font-family: 'Pretendard';
	font-size: 14px;
	line-height: 17px;
	color: #666666;
`;

const Checkbox = styled.input`
	width: 15px;
	height: 15px;
	border: 1px solid #c5c5c5;
	margin: 0 11px 0 17px;
	cursor: pointer;
`;

export default KakaoNotificationSetting;
